<template>
  <v-layout column>
    <v-toolbar
      color="white"
      flat
      height="40"
    >
      <h2
        v-if="title"
        class="body-2 font-weight-bold"
      >
        {{ title }}
      </h2>
      <v-spacer />
      <FileUploader
        :disabled="disabled"
        :resource="resource + '/attachments'"
        :disabled-sender="disabledSender"
        :disabled-upload="disabledUpload"
        :disabled-upload-to-server="disabledUploadToServer"
        :type="type"
        :need-confirm="needConfirm"
        :need-confirm-text="needConfirmText"
        @upload="add"
      />
      <FileSender
        v-if="!disabledSender"
        :disabled="disabled"
        :attachments="attachments"
        :resource="resource + '/rpc/send-attachments'"
      />
    </v-toolbar>
    <div :style="{ height: blockHeight + 'px', 'overflow-y': 'auto' }">
      <v-list
        v-if="attachments.length"
        class="pt-0 mt-2"
      >
        <v-list-tile
          v-for="item in attachments"
          :key="disabledUploadToServer ? item.sourceName : item.id"
          class="compact"
        >
          <v-list-tile-action
            v-if="hasVisibilitySwitch"
            :style="{ minWidth: 'fit-content' }"
            class="mr-1"
          >
            <v-checkbox
              v-model="item.accessibleInCabinet"
              :disabled="isVisibilityChanging || isVisibilitySwitchDisabled"
              @change="handleOnChangeFileVisibility(item)"
            />
          </v-list-tile-action>

          <v-list-tile-content row>
            <v-list-tile-title
              v-if="edit !== item"
              v-text="item.title"
            />
            <v-layout v-else>
              <v-text-field
                v-model="item.title"
                hide-details
                label="Название файла"
                outline
                height="44"
              />
              <v-btn
                :loading="changeNameLoading"
                icon
                @click="applyEdit(item)"
              >
                <v-icon>check</v-icon>
              </v-btn>
            </v-layout>
          </v-list-tile-content>
          <v-list-tile-action>
            <v-layout>
              <v-btn
                v-if="!disabledDownload"
                class="ma-0"
                small
                icon
                @click="download(item.id)"
              >
                <v-icon color="grey darken-1">
                  get_app
                </v-icon>
              </v-btn>
              <v-btn
                class="ma-0"
                :disabled="disabled"
                small
                icon
                @click="startEdit(item)"
              >
                <v-icon color="grey darken-1">
                  edit
                </v-icon>
              </v-btn>
              <v-btn
                v-if="!allowRemove"
                class="ma-0"
                :disabled="disabled"
                small
                icon
                @click="confirmRemove(item)"
              >
                <v-icon color="grey darken-1">
                  delete
                </v-icon>
              </v-btn>
            </v-layout>
          </v-list-tile-action>
        </v-list-tile>
      </v-list>
      <h3
        v-else
        class="caption"
      >
        Нет данных
      </h3>
    </div>
    <app-action-confirm
      :value="showActionDialog"
      :text="needConfirmText"
      @cancel="showActionDialog = false"
      @confirm="remove(removedItem)"
    />
  </v-layout>
</template>

<script>
import FileUploader from "./FileUploader.vue";
import FileSender from "./FileSender.vue";
import * as attachmentsApi from "../../backend/attachmentsApi.js";

export default {
  components: {
    FileUploader,
    FileSender,
  },
  props: [
    "value",
    "disabled",
    "resource",
    "allowRemove",
    "disabledSender",
    "disabledUpload",
    "disabledUploadToServer",
    "disabledDownload",
    "blockHeight",
    "type",
    "title",
    "needConfirm",
    "needConfirmText",
    "hasVisibilitySwitch",
    "isVisibilitySwitchDisabled",
    "onChangeFileVisibility",
  ],
  data() {
    return {
      attachments: [],
      edit: null,
      changeNameLoading: false,
      fileExtention: "",
      confirmMenu: false,
      removedItem: null,
      showActionDialog: false,
      isVisibilityChanging: false,
    };
  },
  watch: {
    value: {
      handler() {
        this.attachments = this.value.length ? this.value : [];
      },
      immediate: true,
    },
  },
  methods: {
    add(newAttachments) {
      this.attachments = this.attachments.concat(newAttachments);
      this.$emit("input", this.attachments);
      this.$emit("change");
    },
    async download(id) {
      const res = await attachmentsApi.getAttachment(id);
      const sliceSize = 1024;
      const byteCharacters = atob(res.data.AttachmentData);
      const bytesLength = byteCharacters.length;
      const slicesCount = Math.ceil(bytesLength / sliceSize);
      const byteArrays = new Array(slicesCount);

      for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        const begin = sliceIndex * sliceSize;
        const end = Math.min(begin + sliceSize, bytesLength);

        const bytes = new Array(end - begin);
        for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
          bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
      }

      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";

      const blob = new Blob(byteArrays, { type: "octet/stream" });
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      const index = this.attachments.map((item) => item.id).indexOf(id);
      a.download = this.attachments[index].title;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    startEdit(item) {
      if (this.disabledUploadToServer) {
        this.attachments.forEach((file, index) => {
          if (index === this.attachments.indexOf(item)) {
            const splitTitle = file.title.split(".");
            this.fileExtention = splitTitle[splitTitle.length - 1];
          }
        });
      } else {
        this.attachments.forEach((file) => {
          if (file.id === item.id) {
            const splitTitle = file.title.split(".");
            this.fileExtention = splitTitle[splitTitle.length - 1];
          }
        });
      }
      this.edit = item;
    },
    async applyEdit(item) {
      this.changeNameLoading = true;
      if (this.disabledUploadToServer) {
        this.changeNameLoading = false;
        this.attachments.forEach((file, index) => {
          if (index === this.attachments.indexOf(item)) {
            const newTitle = this.edit.title.includes(this.fileExtention)
              ? this.edit.title
              : `${this.edit.title}.${this.fileExtention}`;
            file.title = newTitle;
            file.sourceName = newTitle;
          }
        });
        this.$emit("input", this.attachments);
        this.edit = null;
      } else {
        this.edit.title = this.edit.title.includes(this.fileExtention)
          ? this.edit.title
          : `${this.edit.title}.${this.fileExtention}`;
        await attachmentsApi.updateAttachment(this.edit.id, this.edit.title);
        this.changeNameLoading = false;
        this.edit = null;
        this.showInfo("Изменения сохранены");
      }
    },
    async remove(item) {
      if (this.disabledUploadToServer) {
        const index = this.attachments.indexOf(
          this.attachments.find(
            (element) => element.sourceName === item.sourceName
          )
        );
        this.attachments.splice(index, 1);
      } else {
        await attachmentsApi.deleteAttachment(this.resource, item.id);
        const index = this.attachments.map((item) => item.id).indexOf(item.id);
        this.attachments.splice(index, 1);
        this.showInfo("Файл удален");
        this.$emit("change", true);
      }
      this.showActionDialog = false;
    },
    confirmAdd() {
      this.$emit("change", true);
      this.menu = false;
    },
    confirmRemove(item) {
      if (!this.needConfirm) {
        this.remove(item);
        return;
      }
      this.removedItem = item;
      this.showActionDialog = true;
    },
    async handleOnChangeFileVisibility(file) {
      this.isVisibilityChanging = true;

      try {
        await attachmentsApi.updateAttachmentVisibility(file.id);
        this.showInfo("Изменения сохранены");
        const copy = [...this.attachments];
        const index = copy.findIndex((i) => i.id === file.id);
        copy.splice(index, 1, {
          ...file,
          accessibleInCabinet: !file.accessibleInCabinet,
        });
        this.$props["onChangeFileVisibility"]?.(this.attachments);
      } finally {
        this.isVisibilityChanging = false;
      }
    },
  },
};
</script>
